/**
 * Morpholio-Trace-style ruler helpers (Iter36).
 *
 * The ruler is either:
 *   • "straight"    — an infinite line defined by a center point + angle
 *   • "perspective" — a line that pivots around a vanishing point; when
 *                     drawn near the vanishing point, new vertices snap to
 *                     the current ruler beam (which visually recedes toward
 *                     the vanishing point).
 *
 * All coordinates are in normalized [0,1] media space so zoom / pan don't
 * distort the maths — the caller converts back to screen pixels for
 * rendering. The snap tolerance is expressed in unit-space (media_h units).
 */

/** Return the projection of point P onto the infinite line through A with unit direction d.
 *  A + t·d, where t = (P-A)·d. */
export function projectOntoLine(px, py, ax, ay, dx, dy) {
  const t = (px - ax) * dx + (py - ay) * dy;
  return { x: ax + t * dx, y: ay + t * dy, t };
}

/** Perpendicular distance from a point to a line defined by (a, dir). */
export function distToLine(px, py, ax, ay, dx, dy) {
  // Signed distance = cross((P-A), d)
  return Math.abs((px - ax) * dy - (py - ay) * dx);
}

/** Snap a raw vertex to the ruler if the perpendicular distance is within
 *  `tolUnit`. `ruler` is {mode, cx, cy, angleDeg, vanishX, vanishY, enabled}.
 *  Returns `null` if the ruler is off or the point is out of range. */
export function snapToRuler(raw, ruler, tolUnit) {
  if (!ruler || !ruler.enabled) return null;
  if (ruler.mode === "perspective") {
    // Beam from vanishing point through the ruler center.
    const vx = ruler.vanishX, vy = ruler.vanishY;
    const cx = ruler.cx,      cy = ruler.cy;
    let dx = cx - vx, dy = cy - vy;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return null;
    dx /= len; dy /= len;
    const d = distToLine(raw.x, raw.y, vx, vy, dx, dy);
    if (d > tolUnit) return null;
    return projectOntoLine(raw.x, raw.y, vx, vy, dx, dy);
  }
  // Straight ruler
  const rad = (ruler.angleDeg || 0) * Math.PI / 180;
  const dx = Math.cos(rad), dy = Math.sin(rad);
  const d = distToLine(raw.x, raw.y, ruler.cx, ruler.cy, dx, dy);
  if (d > tolUnit) return null;
  return projectOntoLine(raw.x, raw.y, ruler.cx, ruler.cy, dx, dy);
}

/** Intersection of two infinite lines {cx, cy, dx, dy} (unit dir optional).
 *  Returns {x, y} or null when (near-)parallel. */
export function lineLineIntersection(a, b) {
  const den = a.dx * b.dy - a.dy * b.dx;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((b.cx - a.cx) * b.dy - (b.cy - a.cy) * b.dx) / den;
  return { x: a.cx + a.dx * t, y: a.cy + a.dy * t };
}

/** Intersection of two SEGMENTS p1→p2 and p3→p4. Returns {x, y} only when
 *  the crossing lies on both segments, else null. */
export function segSegIntersection(p1, p2, p3, p4) {
  const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
  const den = d1x * d2y - d1y * d2x;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / den;
  const u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / den;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return { x: p1.x + d1x * t, y: p1.y + d1y * t };
}

/** Round angle to the closest multiple of `stepDeg` (e.g. 15°). Result in
 *  degrees, ∈ [0, 360). */
export function snapAngle(deg, stepDeg = 15) {
  const norm = ((deg % 360) + 360) % 360;
  return Math.round(norm / stepDeg) * stepDeg;
}

/** Compute the ruler line's on-screen endpoints in normalized space, given
 *  the current ruler + media aspect (used to extend the line off-canvas). */
export function rulerEndpoints(ruler, aspect = 1) {
  if (ruler.mode === "perspective") {
    const dx = ruler.cx - ruler.vanishX, dy = ruler.cy - ruler.vanishY;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return { a: { x: ruler.cx, y: ruler.cy }, b: { x: ruler.cx, y: ruler.cy } };
    const nx = dx / len, ny = dy / len;
    // Extend far past the canvas so it visually reads as infinite.
    const far = 2.4;
    return {
      a: { x: ruler.vanishX + nx * far, y: ruler.vanishY + ny * far },
      b: { x: ruler.vanishX - nx * far, y: ruler.vanishY - ny * far },
    };
  }
  const rad = (ruler.angleDeg || 0) * Math.PI / 180;
  const dx = Math.cos(rad), dy = Math.sin(rad);
  const far = 2.4;
  return {
    a: { x: ruler.cx + dx * far, y: ruler.cy + dy * far },
    b: { x: ruler.cx - dx * far, y: ruler.cy - dy * far },
  };
}
